"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "./ui/Avatar";
import { useAnchoredPosition } from "@/lib/hooks/useAnchoredPosition";
import type { UserSummary } from "@theta/api-client";

/**
 * A MENTION IS AN ID AND A DISPLAY NAME (O-21/O-22).
 *
 * `username` is gone from the wire, so a candidate has nothing else to be
 * addressed by — and an id is the only form that cannot resolve to the wrong
 * person: ten real accounts carry a numeric username equal to a DIFFERENT
 * user's id. The editor writes `@[id]`; the label the reader sees is the
 * display name.
 *
 * `{ kind: "all" }` is the group-wide `@alle` (OSO-681): no id, no name —
 * the editor writes `@[alle]`. Offered only when the caller says the viewer
 * may use it (`showAll`, gated on the group's `can.moderate`).
 */
export type MentionSelection =
  | { kind: "user"; userId: number; displayName: string }
  | { kind: "all" };

/** Roughly one row (avatar + name + bio) — five of them is a comfortable list. */
const ROW_HEIGHT = 52;
const MAX_ROWS = 4;

export function MentionDropdown({
  users,
  allRank,
  selectedIndex,
  onSelect,
  anchorRef,
}: {
  users: UserSummary[];
  /**
   * Where the `@alle` row sits, when offered at all (the caller has checked
   * `can.moderate`): "first" only for the EXACT query "alle" — for a mere
   * prefix it goes LAST, so Enter on "@a" can never blast the whole group
   * when the author meant Anna. Must match the caller's row order exactly;
   * `RichTextInput.mentionRows` is the one list both draw from.
   */
  allRank?: "first" | "last";
  selectedIndex: number;
  onSelect: (selection: MentionSelection) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rowCount = users.length + (allRank ? 1 : 0);
  // Above the input by preference, so the caret you're typing at stays visible.
  const pos = useAnchoredPosition(anchorRef, rowCount > 0, {
    preferred: "above",
    desiredHeight: Math.min(rowCount, MAX_ROWS) * ROW_HEIGHT,
    revision: users,
  });

  // Scroll selected item into view
  useEffect(() => {
    const container = dropdownRef.current;
    if (!container) return;
    const item = container.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (rowCount === 0 || !pos) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-surface rounded-lg border border-border shadow-lg z-[9999] overflow-y-auto overscroll-contain"
      style={{
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
      }}
    >
      {allRank === "first" && (
        <MentionAllRow selected={selectedIndex === 0} onSelect={onSelect} />
      )}
      {users.map((user, i) => (
        <button
          key={user.id}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect({ kind: "user", userId: user.id, displayName: user.name ?? "" });
          }}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
            i + (allRank === "first" ? 1 : 0) === selectedIndex
              ? "bg-primary-muted"
              : "hover:bg-surface-2"
          }`}
        >
          <Avatar url={user.avatar.medium} name={user.name ?? ""} size="sm" />
          <div className="min-w-0 flex-1">
            {/* The DISPLAY NAME, and there is no handle to fall back to. The
                server guarantees it is never blank. */}
            <p className="text-sm font-medium text-foreground truncate">
              {user.name}
            </p>
          </div>
        </button>
      ))}
      {allRank === "last" && (
        <MentionAllRow selected={selectedIndex === users.length} onSelect={onSelect} />
      )}
    </div>,
    document.body
  );
}

function MentionAllRow({
  selected,
  onSelect,
}: {
  selected: boolean;
  onSelect: (selection: MentionSelection) => void;
}) {
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect({ kind: "all" });
      }}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
        selected ? "bg-primary-muted" : "hover:bg-surface-2"
      }`}
    >
      {/* Same footprint as an avatar, so the column of names lines up. */}
      <span
        aria-hidden="true"
        className="w-8 h-8 shrink-0 rounded-full bg-primary-muted text-primary flex items-center justify-center text-sm font-semibold"
      >
        @
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">alle</p>
        <p className="text-xs text-muted-foreground truncate">
          Giv alle i gruppen besked
        </p>
      </div>
    </button>
  );
}

/** Extract mention query from text at cursor position. Returns null if not in a mention. */
export function extractMentionQuery(
  text: string,
  cursorPos: number
): { query: string; startIndex: number } | null {
  for (let i = cursorPos - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "@" && (i === 0 || /\s/.test(text[i - 1]))) {
      const query = text.slice(i + 1, cursorPos);
      if (/\s/.test(query)) return null;
      return { query, startIndex: i };
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}
